import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiShieldCheckLine } from '@remixicon/react'
import { ONBOARDING_LIMITS, type PrivacyNotice } from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionSpinner,
  Textarea,
} from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

const BASE = '/api/admin/privacy-notices'

function NoticeDialog({
  notice,
  open,
  onOpenChange,
  onSaved,
}: {
  /** `null`: a new one. */
  notice: PrivacyNotice | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The form starts from the notice each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setName(notice?.name ?? '')
    setBody(notice?.body ?? '')
    setError(null)
  }, [open, notice])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      if (notice) await api.put(`${BASE}/${notice.id}`, { name, body })
      else await api.post(BASE, { name, body })
      onSaved()
      onOpenChange(false)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(notice ? 'privacyNotices.editTitle' : 'privacyNotices.newTitle')}</DialogTitle>
          <DialogDescription>{t('privacyNotices.dialogHint')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <Field label={t('privacyNotices.name')} required>
            <Input value={name} maxLength={ONBOARDING_LIMITS.maxNameLength} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('privacyNotices.body')} hint={t('privacyNotices.bodyHint')} required>
            <Textarea
              rows={10}
              value={body}
              maxLength={ONBOARDING_LIMITS.maxPrivacyNoticeLength}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          {error && <Alert>{error}</Alert>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!name.trim() || !body.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The privacy notices an admin keeps ready for onboarding sessions. A session
 * copies the text it is opened with, so changing or deleting one here never
 * changes what applicants already accepted.
 */
export default function PrivacyNotices() {
  const { t } = useTranslation()
  const [notices, setNotices] = useState<PrivacyNotice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<PrivacyNotice | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const load = useCallback(() => {
    return api
      .get<{ data: PrivacyNotice[] }>(BASE)
      .then((response) => setNotices(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? t('errors.generic')))
      .finally(() => setLoading(false))
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  const open = (notice: PrivacyNotice | null) => {
    setEditing(notice)
    setDialogOpen(true)
  }

  const act = async (run: () => Promise<unknown>) => {
    setError(null)
    try {
      await run()
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const add = (
    <Button onClick={() => open(null)}>
      <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
      {t('privacyNotices.new')}
    </Button>
  )

  return (
    <>
      <PageHeader title={t('privacyNotices.title')} subtitle={t('privacyNotices.subtitle')} actions={add} />

      {error && <Alert className="mb-4">{error}</Alert>}

      {loading ? (
        <SectionSpinner />
      ) : notices.length === 0 ? (
        <EmptyState
          icon={<RiShieldCheckLine className="size-6" aria-hidden="true" />}
          title={t('privacyNotices.emptyTitle')}
          description={t('privacyNotices.emptyDescription')}
          action={add}
        />
      ) : (
        <ul className="space-y-4">
          {notices.map((notice) => (
            <li key={notice.id}>
              <Card className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">{notice.name}</h2>
                    {notice.is_default && <Badge variant="success">{t('privacyNotices.default')}</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!notice.is_default && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => act(() => api.post(`${BASE}/${notice.id}/default`, {}))}
                      >
                        {t('privacyNotices.makeDefault')}
                      </Button>
                    )}
                    <Button variant="secondary" size="sm" onClick={() => open(notice)}>
                      {t('common.edit')}
                    </Button>
                    <ConfirmDialog
                      trigger={
                        <Button variant="secondary" size="sm">
                          {t('common.delete')}
                        </Button>
                      }
                      title={t('privacyNotices.deleteTitle', { name: notice.name })}
                      description={t('privacyNotices.deleteDescription')}
                      onConfirm={() => act(() => api.delete(`${BASE}/${notice.id}`))}
                    />
                  </div>
                </div>
                {/* Plain text written by an admin: shown as text, never as markup. */}
                <p className="mt-3 line-clamp-4 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
                  {notice.body}
                </p>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <NoticeDialog notice={editing} open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </>
  )
}
